import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormField, IDropdownResponse } from '../../models/dynamic.model';
import { ValidatorService } from '../../services/form-control-validators.service';
import { mailingAddressFormConfig } from './mailing-address.config';
import { ValidatorPattern } from '../../utility/validators.pattern';
import { SharedModule } from '../../shared.module';
import { CommonService } from '../../services/common.service';
import {
  AccordionTabs,
  conditionCheckConstants,
  validationErrorMessages,
  ActionIcons,
  countryCodes,
  formFieldTypes,
  ModeIcons,
  StatusCodes,
  SvgIconFrom,
  pageType,
  addressField,
} from '../../utility/constants';
import { CustomErrorTooltipComponent } from '../custom-error-tooltip/custom-error-tooltip.component';
import { Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { ModalService } from '../../services/modal.service';
import { CommonPopupService } from '../../services/common-popup.service';
import { Store } from '@ngxs/store';

@Component({
  selector: 'app-mailing-address',
  standalone: true,
  imports: [SharedModule, CustomErrorTooltipComponent],
  templateUrl: './mailing-address.component.html',
  styleUrl: './mailing-address.component.scss',
})
export class MailingAddressComponent implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  @Input() isFrom!: string;
  @Input() isSameAsAddress!: boolean;
  @Output() addressFormDataValue = new EventEmitter<any>();
  @Input() pageMode!: string;
  @Input() lookupData: any;
  public readonly modeIcons = ModeIcons;

  public accountAddressForm!: FormGroup;
  public formFields!: FormField[];
  public formFieldType = formFieldTypes;
  public actionIcons = ActionIcons;
  public countryList!: IDropdownResponse[];
  public stateList!: IDropdownResponse[];
  public errorMessages!: any;
  public setMailingAddressWidth: any = {};
  public setMailingAddressErrorTooltip: any = {};
  public setMailingAddressTooltipEvent: any = {};
  public validationCountryCode = countryCodes;
  public setAddressInfoErrorTooltip: any = {};
  public setAddressInfoTooltipEvent: any = {};
  public setAddressInfoWidth: any = {};
  public svgIconFromList = SvgIconFrom;
  private selectedCountryCode = '';
  private countries$!: Observable<any[]>;

  constructor(
    private readonly fromUtilsService: ValidatorService,
    public readonly commonService: CommonService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly formUtilsService: ValidatorService,
    private readonly commonPopupService: CommonPopupService,
    private readonly modalService: ModalService,
    private readonly store: Store
  ) {}

  ngOnInit(): void {
    this.setReqInit();
    this.checkMode();
    this.commonService.isUFOAccountRestrictedEdit
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateFormState());
    this.dataAirlineCheck();
    this.checkForAirport();
    this.countries$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.countryList = data;
      });
    // Ensure form state is set correctly based on initial pageMode
    this.updateFormState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isFrom === pageType.smAirports && changes['lookupData']) {
      this.countryList = changes['lookupData'].currentValue;
      if (this.accountAddressForm) {
        this.updateFormFieldsForAirport();
      } else {
        this.checkForAirport();
      }
    }
    if (this.isFrom === pageType.facilities && changes['lookupData']) {
      this.countryList = changes['lookupData'].currentValue;
    }
    if (changes['pageMode']?.currentValue) {
      this.pageMode = changes['pageMode'].currentValue;
      // Only update form state if form is initialized
      if (this.accountAddressForm) {
        this.updateFormState();
      }
    }
  }

  private setReqInit() {
    const config = this.buildMailingFormConfig();
    this.formFields = config.fields.map(field => ({ ...field }));
    this.createForm(config);
    this.countries$ = this.store.select(
      (state: any) => state.accountDropdowns.countries
    );
    this.updateNameAndCompanyValidation();
  }

  private buildMailingFormConfig(): { fields: FormField[] } {
    const fields = mailingAddressFormConfig.fields.map(field => ({ ...field }));

    if (this.isFrom === pageType.facilities || this.isFrom === 'airlines') {
      fields.forEach(field => {
        if (field.id === addressField.email) {
          field.required = true;
        }
      });
    }

    if (this.isFrom === pageType.smAirports) {
      fields.forEach(field => {
        if (field.id === 'countryCode') {
          field.required = true;
        } else {
          field.required = false;
          if (field.minValue !== undefined) {
            delete field.minValue;
          }
          if (field.id === addressField.email) {
            delete field.pattern;
          }
        }
      });
    }

    return { fields };
  }

  public checkControlHasError(controlName: string) {
    return this.commonService.checkControlError(
      this.accountAddressForm,
      controlName
    );
  }

  public fetchWidth(event: MouseEvent, controlName: string) {
    this.commonService.setErrorTooltipData(
      event,
      controlName,
      this.setMailingAddressWidth,
      this.setMailingAddressTooltipEvent,
      this.setMailingAddressErrorTooltip
    );
  }

  public fetchWidthPhone(
    event: MouseEvent,
    controlName: string,
    isFrom?: string
  ) {
    this.commonService.setErrorTooltipData(
      event,
      controlName,
      this.setAddressInfoWidth,
      this.setAddressInfoTooltipEvent,
      this.setAddressInfoErrorTooltip,
      0,
      isFrom
    );
  }

  public fetchErrorMessages(controlName: string): string {
    if (
      this.accountAddressForm.get(controlName)?.errors?.[
        conditionCheckConstants.autocompleteError
      ]
    ) {
      return validationErrorMessages.autocompleteError;
    } else if (
      this.accountAddressForm.get(controlName)?.errors?.['invalidState']
    ) {
      return validationErrorMessages.invalidState;
    } else {
      return this.fromUtilsService.getErrorMessage(
        this.accountAddressForm,
        this.errorMessages,
        controlName
      );
    }
  }

  public mailingAddressHeader(isFrom: string): string {
    return isFrom === conditionCheckConstants.billingAddress
      ? ''
      : AccordionTabs.Address;
  }

  public getLookupFieldClearIcon(controlName: string) {
    return (
      (controlName === 'countryCode' &&
        this.commonService.isValidField(
          this.accountAddressForm.get('countryCode')?.value
        )) ||
      (controlName === 'state' &&
        this.commonService.isValidField(
          this.accountAddressForm.get('state')?.value
        ))
    );
  }

  public clearLookupFieldValues(controlName: string) {
    if (controlName === 'countryCode') {
      this.accountAddressForm.get('countryCode')?.setValue('');
      this.setOrRemoveCountryBasedValidation(
        this.accountAddressForm.get('countryCode')?.value
      );
    }
    this.accountAddressForm.get('state')?.setValue('');
  }

  public onLookupSearchIconClicked(controlName: string) {
    const lookupData =
      controlName === 'countryCode' ? this.countryList : this.stateList;
    if (lookupData) {
      const dialogRef = this.commonPopupService.openLookupPopup(
        this.modalService,
        controlName,
        lookupData,
        controlName === 'countryCode'
          ? this.accountAddressForm.get('countryCode')?.value
          : this.accountAddressForm.get('state')?.value
      );
      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((popupData: any) => {
        if (popupData && controlName === 'countryCode') {
          this.commonService.markControlTouched(
            this.accountAddressForm,
            controlName
          );
          this.commonService.setControlRequiredValidation(
            this.accountAddressForm,
            controlName
          );
        } else if (popupData && controlName === 'state') {
          this.commonService.markControlTouched(
            this.accountAddressForm,
            controlName
          );
          this.setOrRemoveCountryBasedValidation(
            this.accountAddressForm.get('countryCode')?.value
          );
        }
        if (popupData?.data && controlName === 'countryCode') {
          this.accountAddressForm
            .get('countryCode')
            ?.setValue(popupData?.data.country);
          this.accountAddressForm.get('state')?.setValue('');
          this.dispatchAccountsAddressFormChanges();
          this.setOrRemoveCountryBasedValidation(
            this.accountAddressForm.get('countryCode')?.value
          );
          this.accountAddressForm.markAsDirty();
        } else if (popupData?.data && controlName === 'state') {
          this.accountAddressForm.get('state')?.setValue(popupData?.data.state);
          this.accountAddressForm.markAsDirty();
          this.dispatchAccountsAddressFormChanges();
        }
      });
    }
    setTimeout(() => {
      this.commonService.markControlUnTouched(
        this.accountAddressForm,
        controlName
      );
      this.commonService.clearControlValidators(
        this.accountAddressForm,
        controlName
      );
    }, 200);
  }

  public dispatchAccountsAddressFormChanges() {
    this.addressFormDataValue.emit({
      isFrom: this.isFrom,
      formData: this.accountAddressForm.getRawValue(),
      isFormValid: this.accountAddressForm,
    });
    this.setOrRemoveCountryBasedValidation(
      this.accountAddressForm.get('countryCode')?.value
    );
  }

  public getSelectedCountryCode(): string {
    const countryCodeValue = this.accountAddressForm.get('countryCode')?.value;
    if (!countryCodeValue) return '';

    const code =
      countryCodeValue.split(' - ')[0] || countryCodeValue.split(' ')[0];
    return code || '';
  }

  public createForm(config?: { fields: FormField[] }) {
    const formConfig = config ?? this.buildMailingFormConfig();
    const { form, errorMessages } = this.fromUtilsService.generateForm(
      formConfig,
      this.destroyRef
    );
    this.accountAddressForm = form;
    this.errorMessages = errorMessages;
    if (this.isFrom === pageType.smAirports) {
      this.setupAirportConditionalValidators();
    }
  }

  public getAddressPlaceholder(controlName: string) {
    return this.commonService.getPlaceholder(
      this.accountAddressForm,
      controlName,
      this.pageMode,
      this.modeIcons
    );
  }

  public getStateList(data: string, openPopup: boolean) {
    const previousSelectedCountryCode = this.selectedCountryCode;
    const currentSelectedCountryCode = this.commonService.isValidField(
      this.accountAddressForm.get('countryCode')?.value
    )
      ? this.accountAddressForm.get('countryCode')?.value
      : data;
    this.selectedCountryCode = currentSelectedCountryCode;
    if (data && previousSelectedCountryCode !== currentSelectedCountryCode) {
      this.commonService
        .getStateData(data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data: any) => {
            if (data.code == StatusCodes.Success) {
              this.stateList = data?.data || [];
              if (openPopup) {
                this.onLookupSearchIconClicked('state');
              } else {
                const selectedState = this.stateList?.find(
                  state =>
                    state.stateCode ===
                    this.accountAddressForm.get('state')?.value
                );
                const selectedStateValue = selectedState
                  ? selectedState.stateCode + ' - ' + selectedState.name
                  : this.accountAddressForm.get('state')?.value;
                this.accountAddressForm
                  .get('state')
                  ?.setValue(selectedStateValue);
              }
            } else if (data.statusCode == StatusCodes.SuccessfulRequest) {
              this.removeControlsValidationCountryBased();
            } else {
              this.errorHandlerService.handleApiError(data);
            }
          },
          error: (error: HttpErrorResponse) => {
            this.errorHandlerService.handleError(error);
          },
        });
    } else if (
      data &&
      previousSelectedCountryCode === currentSelectedCountryCode
    ) {
      this.onLookupSearchIconClicked('state');
    } else if (!data) {
      this.accountAddressForm.get('state')?.markAsTouched();
      this.accountAddressForm.get('state')?.setErrors({ invalidState: true });
    }
  }

  private setOrRemoveCountryBasedValidation(countryCodeVal: string) {
    if (this.isFrom === pageType.smAirports) {
      return;
    }
    const countryCodeValue = countryCodeVal.split(' - ')[0];
    if (
      countryCodeValue === countryCodes.usa ||
      countryCodeValue === countryCodes.canada ||
      countryCodeValue === countryCodes.brazil
    ) {
      this.setControlsValidationCountryBased();
    } else {
      this.removeControlsValidationCountryBased();
      if (this.setMailingAddressErrorTooltip['state']) {
        this.commonService.closeErrorTooltip(
          'state',
          this.setMailingAddressErrorTooltip
        );
      }
    }
  }

  private setControlsValidationCountryBased() {
    this.commonService.setControlRequiredValidation(
      this.accountAddressForm,
      'state'
    );
    this.commonService.setControlRequiredValidation(
      this.accountAddressForm,
      'zipCode'
    );
  }

  private removeControlsValidationCountryBased() {
    if (this.isFrom === pageType.smAirports) {
      return;
    }
    const countryCodeValue = this.accountAddressForm
      .get('countryCode')
      ?.value?.split(' - ')[0];
    if (
      countryCodeValue !== countryCodes.usa ||
      countryCodeValue !== countryCodes.canada ||
      countryCodeValue !== countryCodes.brazil
    ) {
      this.commonService.clearControlValidators(
        this.accountAddressForm,
        'state'
      );
      this.commonService.clearControlValidators(
        this.accountAddressForm,
        'zipCode'
      );
      this.accountAddressForm
        .get('zipCode')
        ?.setValidators([Validators.minLength(4), Validators.maxLength(10)]);
      this.accountAddressForm.get('zipCode')?.updateValueAndValidity();
    }
  }

  public formAutocompleteValueChanges(
    controlName: string,
    apiControlName: string,
    dropdownResponse: any,
    form: any,
    formControlId?: string
  ) {
    if (dropdownResponse && controlName != 'countryCode') {
      return this.commonService.getAutocompleteDropdownId(
        dropdownResponse,
        form,
        controlName,
        apiControlName,
        false,
        formControlId
      );
    } else {
      this.getErrorMessage(controlName);
    }
  }

  public getErrorMessage(controlName: string): string {
    const control = this.accountAddressForm.get(controlName);

    if (control?.errors?.['autocompleteError']) {
      return validationErrorMessages.autocompleteError;
    } else if (
      control?.errors?.['phoneMinLength'] ||
      control?.errors?.['phoneMaxLength']
    ) {
      const error =
        control.errors['phoneMinLength'] || control.errors['phoneMaxLength'];
      if (error.isDigitsOnly) {
        return validationErrorMessages.phoneMinLengthDigits;
      } else {
        return validationErrorMessages.phoneAlphanumeric;
      }
    } else {
      return this.formUtilsService.getErrorMessage(
        this.accountAddressForm,
        this.errorMessages,
        controlName
      );
    }
  }

  private checkMode() {
    this.commonService.isCheckForMode
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        // Only update pageMode if it's not already set via @Input
        if (!this.pageMode) {
          this.pageMode = data;
        }
        this.updateFormState();
      });
  }

  private updateFormState() {
    // Only update form state if the form has been initialized
    if (!this.accountAddressForm) {
      return;
    }

    const ufoEditLocked = this.commonService.isUFOAccountRestrictedEdit.value;
    if (
      this.pageMode === ModeIcons.view ||
      this.pageMode === ModeIcons.smallview ||
      ufoEditLocked
    ) {
      this.accountAddressForm.disable();
    } else {
      this.accountAddressForm.enable();
    }
  }

  private dataAirlineCheck() {
    this.commonService.isCheckDataValues
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data?.info && this.pageMode !== ModeIcons.add) {
          this.accountAddressForm.patchValue(data.info);
        }
        if (data?.isMakeDirty) {
          this.accountAddressForm.markAllAsTouched();
        }
      });
  }

  public updateNameAndCompanyValidation(): void {
    if (this.isFrom === pageType.facilities) {
      const nameControl = this.accountAddressForm.get('name');
      const companyNameControl = this.accountAddressForm.get('companyName');
      if (!nameControl || !companyNameControl) {
        this.dispatchAccountsAddressFormChanges();
        return;
      }
      if (nameControl && companyNameControl) {
        const nameValue = nameControl.value;
        const companyNameValue = companyNameControl.value;

        if (nameValue) {
          companyNameControl.removeValidators(Validators.required);
        } else {
          companyNameControl.addValidators(Validators.required);
        }

        if (companyNameValue) {
          nameControl.removeValidators(Validators.required);
        } else {
          nameControl.addValidators(Validators.required);
        }
        nameControl.updateValueAndValidity();
        companyNameControl.updateValueAndValidity();
      }
    }
    this.dispatchAccountsAddressFormChanges();
  }

  private checkForAirport() {
    const config = this.buildMailingFormConfig();
    this.formFields = config.fields.map(field => ({ ...field }));
  }

  private updateFormFieldsForAirport() {
    this.checkForAirport();
  }

  private setupAirportConditionalValidators(): void {
    const conditionalFields: Array<{
      controlName: string;
      validators: ValidatorFn[];
    }> = [
      {
        controlName: addressField.email,
        validators: [
          Validators.maxLength(50),
          Validators.pattern(ValidatorPattern.Email_Validator),
        ],
      },
      {
        controlName: 'city',
        validators: [Validators.minLength(3), Validators.maxLength(50)],
      },
      {
        controlName: 'zipCode',
        validators: [Validators.minLength(4), Validators.maxLength(10)],
      },
    ];

    conditionalFields.forEach(({ controlName, validators }) => {
      const control = this.accountAddressForm.get(controlName);
      if (!control) {
        return;
      }

      let conditionalActive = false;

      const applyValidators = (value: unknown) => {
        const text = typeof value === 'string' ? value.trim() : '';
        if (!text && conditionalActive) {
          control.removeValidators(validators);
          conditionalActive = false;
        } else if (text && !conditionalActive) {
          control.addValidators(validators);
          conditionalActive = true;
        }
        control.updateValueAndValidity({ emitEvent: false });
      };

      applyValidators(control.value);
      control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(applyValidators);
    });
  }
}
